import NoPictureIcon from '@/common/assets/icons/noPicture.svg?react';
import RotateIcon from '@/common/assets/icons/rotate-left.svg?react';
import ZoomInIcon from '@/common/assets/icons/search-zoom-in.svg?react';
import ZoomOutIcon from '@/common/assets/icons/search-zoom-out.svg?react';
import MainModal from '@/common/components/base/modal/Modal';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import { DeleteExtension } from '@/common/components/imageGallery/components/Extensions/deleteConfigration/DeleteConfigration';
import { DownloadExtension } from '@/common/components/imageGallery/components/Extensions/DownloadExtension';
import { EditNameExtension } from '@/common/components/imageGallery/components/Extensions/editNameExtension/EditNameExtension';
import { MoreItemsExtension } from '@/common/components/imageGallery/components/Extensions/moreItemsExtension/MoreItemsExtension';
import { ImageCardProps } from '@/common/components/imageGallery/types/imageCard.type';
import useModal from '@/common/hooks/useModal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from 'styled-system/css';
import { Box, Center } from 'styled-system/jsx';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.25;

const ImageCard = ({ extension, imageData }: ImageCardProps) => {
  const { closeModal, open, openModal } = useModal();
  if (!imageData.src) {
    if (extension) {
      extension.type = '';
    }
  }
  return (
    <>
      <Box
        className={css({
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          height: 'auto',
          shadow: 'cardShadow',
          overflow: 'hidden',
        })}
      >
        {imageData.src ? (
          <img
            onClick={openModal}
            className={css({
              width: '100%',
              height: '112px',
              objectFit: 'cover',
              borderRadius: '8px 8px 0 0',
            })}
            src={imageData.src}
          />
        ) : (
          <Center>
            <NoPictureIcon />
          </Center>
        )}
        <Typography
          p="0.5rem"
          textStyle="b4"
          color="black-1"
          textAlign="center"
        >
          {imageData?.name}
        </Typography>

        {extension?.type === 'delete' ? (
          <DeleteExtension
            onDeletePhotoHandler={extension.onDeletePhotoHandler}
          />
        ) : null}
        {extension?.type === 'download' ? (
          <DownloadExtension
            onClick={extension.downloadOnClick}
            src={imageData.src}
          />
        ) : null}
        {extension?.type === 'moreItems' ? (
          <MoreItemsExtension
            handleClick={extension.handleClick}
            items={extension.items}
            src={imageData.src}
            deleteImageLoading={extension.deleteImageLoading}
          />
        ) : null}
        {extension?.type === 'editName' ? (
          <EditNameExtension onClick={extension.editNameOnClick} />
        ) : null}
      </Box>

      <MainModal closeModal={closeModal} open={open}>
        <PreviewModal {...imageData} />
      </MainModal>
    </>
  );
};

export default ImageCard;

const PreviewModal = (imageData: ImageCardProps['imageData']) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [rotation, setRotation] = useState(0);

  const stateRef = useRef({ scale, translate });
  useEffect(() => {
    stateRef.current = { scale, translate };
  }, [scale, translate]);

  const dragStart = useRef({ mouseX: 0, mouseY: 0, tx: 0, ty: 0 });
  const lastPinchDist = useRef<number | null>(null);

  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setRotation(0);
  }, [imageData.src]);

  const clampT = useCallback((tx: number, ty: number, s: number) => {
    if (s <= 1) return { x: 0, y: 0 };
    const rect = containerRef.current?.getBoundingClientRect() ?? {
      width: 0,
      height: 0,
    };
    const maxX = (rect.width / 2) * (s - 1);
    const maxY = (rect.height / 2) * (s - 1);
    return {
      x: Math.min(maxX, Math.max(-maxX, tx)),
      y: Math.min(maxY, Math.max(-maxY, ty)),
    };
  }, []);

  const applyZoom = useCallback(
    (delta: number) => {
      setScale((prev) => {
        const next = Math.min(
          MAX_SCALE,
          Math.max(MIN_SCALE, parseFloat((prev + delta).toFixed(2))),
        );
        setTranslate((t) => clampT(t.x, t.y, next));
        return next;
      });
    },
    [clampT],
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      applyZoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    },
    [applyZoom],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (stateRef.current.scale <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      tx: stateRef.current.translate.x,
      ty: stateRef.current.translate.y,
    };
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStart.current.mouseX;
      const dy = e.clientY - dragStart.current.mouseY;
      setTranslate(
        clampT(
          dragStart.current.tx + dx,
          dragStart.current.ty + dy,
          stateRef.current.scale,
        ),
      );
    },
    [isDragging, clampT],
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1 && stateRef.current.scale > 1) {
      setIsDragging(true);
      dragStart.current = {
        mouseX: e.touches[0].clientX,
        mouseY: e.touches[0].clientY,
        tx: stateRef.current.translate.x,
        ty: stateRef.current.translate.y,
      };
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length === 2 && lastPinchDist.current !== null) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const ratio = dist / lastPinchDist.current;
        lastPinchDist.current = dist;
        setScale((prev) => {
          const next = Math.min(
            MAX_SCALE,
            Math.max(MIN_SCALE, parseFloat((prev * ratio).toFixed(2))),
          );
          setTranslate((t) => clampT(t.x, t.y, next));
          return next;
        });
      } else if (e.touches.length === 1 && isDragging) {
        const dx = e.touches[0].clientX - dragStart.current.mouseX;
        const dy = e.touches[0].clientY - dragStart.current.mouseY;
        setTranslate(
          clampT(
            dragStart.current.tx + dx,
            dragStart.current.ty + dy,
            stateRef.current.scale,
          ),
        );
      }
    },
    [isDragging, clampT],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    lastPinchDist.current = null;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const canZoomIn = scale < MAX_SCALE;
  const canZoomOut = scale > MIN_SCALE;

  return (
    <>
      <TitleDescription>{imageData.name}</TitleDescription>

      <Box
        ref={containerRef}
        onMouseDown={handleMouseDown}
        className={css({
          position: 'relative',
          overflow: 'hidden',
          my: '1rem',
          borderRadius: '8px',
          backgroundColor: 'gray.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '420px',
          userSelect: 'none',
        })}
        style={{
          cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
      >
        <img
          src={imageData.src}
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: '8px',
            pointerEvents: 'none',
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.15s ease',
            willChange: 'transform',
          }}
        />
      </Box>

      <Box
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '24px',
          marginBottom: '32px',
          marginTop: '24px',
        })}
      >
        <Box
          onClick={() => applyZoom(ZOOM_STEP)}
          className={css({
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'purple-5',
            cursor: canZoomIn ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <ZoomInIcon />
        </Box>
        <Box
          onClick={() => applyZoom(-ZOOM_STEP)}
          className={css({
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'purple-5',
            cursor: canZoomOut ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <ZoomOutIcon />
        </Box>

        <Box
          onClick={() => setRotation((prev) => prev + 90)}
          className={css({
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'purple-5',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <RotateIcon />
        </Box>
      </Box>
    </>
  );
};
